import * as THREE from "three";
import { isSolid, WATER } from "../world/blocks";
import { WORLD_HEIGHT } from "../world/chunk";
import type { BlockView } from "./dropped-items";

/**
 * Primed TNT entity simulation.
 *
 * Modeled after Minecraft's EntityTNTPrimed:
 * - 4.0s fuse countdown (or shorter if triggered by an explosion chain reaction).
 * - Upward hop on ignition with slight random horizontal nudge.
 * - Full block physics: gravity, drag, ground bounce and friction, water floating.
 * - Detonation produces power 4.0 explosion.
 */

export const TNT_DEFAULT_FUSE = 4.0;
export const TNT_CHAIN_MIN_FUSE = 0.5;
export const TNT_CHAIN_MAX_FUSE = 1.0;
export const TNT_HALF = 0.45;
export const TNT_POWER = 4.0;

const TNT_GRAVITY = 22;
const TNT_TERMINAL_VELOCITY = 35;
const TNT_MAX_SUBSTEP = 0.2;
const EPS = 1e-4;

export interface PrimedTnt {
  readonly id: number;
  readonly position: THREE.Vector3;
  readonly velocity: THREE.Vector3;
  fuse: number;
  readonly maxFuse: number;
  onGround: boolean;
}

export interface TntExplosion {
  readonly id: number;
  readonly position: THREE.Vector3;
  readonly power: number;
}

export class PrimedTntSimulation {
  readonly tnts: PrimedTnt[] = [];
  private nextId = 1;
  private readonly pendingExplosions: TntExplosion[] = [];

  constructor(private readonly world: BlockView) {}

  /**
   * Spawn a primed TNT entity at the specified coordinates.
   * `fuse` is the fuse duration in seconds (default 4.0s).
   */
  spawn(x: number, y: number, z: number, fuse = TNT_DEFAULT_FUSE): PrimedTnt {
    const angle = Math.random() * Math.PI * 2;
    const horizontalSpeed = 0.4 + Math.random() * 0.4;
    const tnt: PrimedTnt = {
      id: this.nextId++,
      position: new THREE.Vector3(x, y, z),
      velocity: new THREE.Vector3(
        Math.cos(angle) * horizontalSpeed,
        3.5 + Math.random() * 0.8, // Initial upward hop
        Math.sin(angle) * horizontalSpeed,
      ),
      fuse,
      maxFuse: fuse,
      onGround: false,
    };
    this.tnts.push(tnt);
    return tnt;
  }

  /**
   * Advance physics and fuse countdowns for all primed TNT entities.
   * Returns explosions that triggered this step.
   */
  update(dt: number): TntExplosion[] {
    if (!Number.isFinite(dt) || dt <= 0) return [];

    const explosions: TntExplosion[] = [];
    const step = Math.min(dt, 0.1);

    for (let i = this.tnts.length - 1; i >= 0; i--) {
      const tnt = this.tnts[i];

      // Fuse countdown
      tnt.fuse -= dt;
      if (tnt.fuse <= 0) {
        this.tnts.splice(i, 1);
        explosions.push({
          id: tnt.id,
          position: tnt.position.clone(),
          power: TNT_POWER,
        });
        continue;
      }

      // Physics
      this.applyPhysics(tnt, step);
      this.move(tnt, step);

      // Cull if fallen out of world
      if (tnt.position.y < -16) {
        this.tnts.splice(i, 1);
      }
    }

    return explosions;
  }

  clear(): void {
    this.tnts.length = 0;
    this.pendingExplosions.length = 0;
    this.nextId = 1;
  }

  private applyPhysics(tnt: PrimedTnt, dt: number): void {
    const block = this.world.peekBlock(
      Math.floor(tnt.position.x),
      Math.floor(tnt.position.y),
      Math.floor(tnt.position.z),
    );

    if (block === WATER) {
      // In water: slower sink / slight buoyancy and strong drag
      tnt.velocity.y -= TNT_GRAVITY * 0.25 * dt;
      if (tnt.velocity.y < -2.0) tnt.velocity.y = -2.0;
      const drag = Math.max(0, 1 - dt * 3.0);
      tnt.velocity.x *= drag;
      tnt.velocity.z *= drag;
      return;
    }

    tnt.velocity.y -= TNT_GRAVITY * dt;
    if (tnt.velocity.y < -TNT_TERMINAL_VELOCITY) {
      tnt.velocity.y = -TNT_TERMINAL_VELOCITY;
    }

    const airDrag = Math.max(0, 1 - dt * 0.35);
    tnt.velocity.x *= airDrag;
    tnt.velocity.z *= airDrag;
  }

  private move(tnt: PrimedTnt, dt: number): void {
    this.moveAxis(tnt, 0, tnt.velocity.x * dt);
    this.moveAxis(tnt, 1, tnt.velocity.y * dt);
    this.moveAxis(tnt, 2, tnt.velocity.z * dt);

    tnt.onGround = this.grounded(tnt);
    if (tnt.onGround) {
      const friction = Math.max(0, 1 - dt * 8);
      tnt.velocity.x *= friction;
      tnt.velocity.z *= friction;
      if (Math.abs(tnt.velocity.x) < 0.02) tnt.velocity.x = 0;
      if (Math.abs(tnt.velocity.z) < 0.02) tnt.velocity.z = 0;
    }
  }

  private moveAxis(tnt: PrimedTnt, axis: 0 | 1 | 2, delta: number): void {
    if (delta === 0) return;
    const steps = Math.max(1, Math.ceil(Math.abs(delta) / TNT_MAX_SUBSTEP));
    const inc = delta / steps;
    const key = axis === 0 ? "x" : axis === 1 ? "y" : "z";

    for (let s = 0; s < steps; s++) {
      tnt.position[key] += inc;
      if (!this.collides(tnt.position)) continue;

      if (inc > 0) {
        tnt.position[key] = Math.floor(tnt.position[key] + TNT_HALF) - TNT_HALF - EPS;
      } else {
        tnt.position[key] = Math.floor(tnt.position[key] - TNT_HALF) + 1 + TNT_HALF + EPS;
      }

      if (axis === 1) {
        // Ground bounce if falling with speed
        if (inc < 0 && tnt.velocity.y < -1.5) {
          tnt.velocity.y = -tnt.velocity.y * 0.45;
        } else {
          tnt.velocity.y = 0;
        }
      } else if (axis === 0) {
        tnt.velocity.x = 0;
      } else {
        tnt.velocity.z = 0;
      }
    }
  }

  private collides(pos: THREE.Vector3): boolean {
    const minX = Math.floor(pos.x - TNT_HALF + EPS);
    const maxX = Math.floor(pos.x + TNT_HALF - EPS);
    const minY = Math.floor(pos.y + EPS);
    const maxY = Math.floor(pos.y + TNT_HALF * 2 - EPS);
    const minZ = Math.floor(pos.z - TNT_HALF + EPS);
    const maxZ = Math.floor(pos.z + TNT_HALF - EPS);

    for (let y = minY; y <= maxY; y++) {
      for (let z = minZ; z <= maxZ; z++) {
        for (let x = minX; x <= maxX; x++) {
          if (this.blockSolid(x, y, z)) return true;
        }
      }
    }
    return false;
  }

  private blockSolid(wx: number, wy: number, wz: number): boolean {
    if (wy < 0) return true;
    if (wy >= WORLD_HEIGHT) return false;
    return isSolid(this.world.peekBlock(wx, wy, wz));
  }

  private grounded(tnt: PrimedTnt): boolean {
    return this.blockSolid(
      Math.floor(tnt.position.x),
      Math.floor(tnt.position.y - 0.05),
      Math.floor(tnt.position.z),
    );
  }
}
